import merge from 'lodash/merge';
import { stringToRichText } from '@belong/contentful';
import {
  ALERT_LEVELS,
  IAddressCaptureContent,
  IAddressDetail,
  IExtendedPartialAddressResult,
  ILegacyTelstraAddress,
  IMediumCharacteristics,
  INbnOrderAddress,
  IServiceQualificationAddress,
  IStructuredAddressResponse,
  ITelstraAddress
} from '@belong/types';
import ADDRESS_SQ_CONTENT from './content-unified-sq.json';
import ADDRESS_CONTENT from './content.json';

/**
 * Convert either a full TelstraAddress object or manual address fields to a single line address
 * With option to hide suffix
 * @example "Unit 2/123 Example St, Suburb NSW 2000"
 * @example "Unit 2/123 Example St"
 */
export const toSingleLineAddress = (address: IAddressDetail, shouldHideSuffix?: boolean): string => {
  const { locality, state, postcode } = address;

  const streetAddress = getStreetAddress(address);
  const suffix = `${locality} ${state} ${postcode}`;

  return shouldHideSuffix ? streetAddress : `${streetAddress}, ${suffix}`;
};

/**
 * Convert manual address fields to a single line address without locale/state/postcode
 * @example "Unit 2/123 Example St"
 */
export const getStreetAddress = (fields: IAddressDetail): string => {
  const { addressLine1, addressLine2 } = fields;
  return [addressLine1?.trim(), addressLine2?.trim()].filter(Boolean).join(', ');
};

export const isExtendedStructuredAddressResponse = (
  response: IStructuredAddressResponse
): response is [IExtendedPartialAddressResult] => {
  return response?.length === 1 && 'addressJwt' in response[0];
};

export const isLegacyAddress = (
  address:
    | IServiceQualificationAddress
    | INbnOrderAddress
    | ILegacyTelstraAddress
    | ITelstraAddress
    | IMediumCharacteristics
    | IAddressDetail
): address is INbnOrderAddress | ILegacyTelstraAddress => {
  return ['subAddress', 'localityName'].every(field => field in address);
};

// Helper to convert legacy address types to simple address detail
export const toAddressDetail = (
  address:
    | IServiceQualificationAddress
    | INbnOrderAddress
    | ILegacyTelstraAddress
    | ITelstraAddress
    | IMediumCharacteristics
    | IAddressDetail
): IAddressDetail => {
  const addressDetail: IAddressDetail = {
    addressId: (address as any).addressId ?? '',
    gnafId: (address as any).gnafId,
    addressLine1: (address as any).addressLine1 || (address as any).streetAddress,
    addressLine2: (address as any).addressLine2,
    locality: (address as any).locality || (address as any).localityName,
    state: address.state,
    postcode: address.postcode
  };

  // Create an addressLine1 for legacy address formats
  if (isLegacyAddress(address)) {
    const { subAddress, streetNumber, streetName, streetType } = address;
    const subAddressDetail = subAddress?.trim();
    const streetAddress = [streetNumber || '', streetName || '', streetType || ''].filter(Boolean).join(' ');

    if (subAddressDetail) {
      addressDetail.addressLine1 = subAddressDetail;
      addressDetail.addressLine2 = streetAddress;
    } else {
      addressDetail.addressLine1 = streetAddress;
      addressDetail.addressLine2 = '';
    }
  }

  return addressDetail;
};

/**
 * Static content helpers
 * These are used in tests and as fallback content during CMS content migration
 *
 * DELETE Once content is supplied from Contentful
 */
export function getStaticAddressContent(overrides: Partial<IAddressCaptureContent> = {}): IAddressCaptureContent {
  const content: IAddressCaptureContent = merge({}, ADDRESS_CONTENT, overrides);

  // Force alert into proper document format for testing purposes
  content.resolve.multipleResultsAlert = {
    ...content.resolve.multipleResultsAlert,
    level: content.resolve.multipleResultsAlert.level as ALERT_LEVELS,
    message: stringToRichText(content.resolve.multipleResultsAlert.message.toString())
  };

  return content;
}

export function getStaticSQAddressContent(overrides: Partial<IAddressCaptureContent> = {}): IAddressCaptureContent {
  const content: IAddressCaptureContent = merge({}, ADDRESS_SQ_CONTENT, overrides);

  // Force alert into proper document format for testing purposes
  content.resolve.multipleResultsAlert = {
    ...content.resolve.multipleResultsAlert,
    level: content.resolve.multipleResultsAlert.level as ALERT_LEVELS,
    message: stringToRichText(content.resolve.multipleResultsAlert.message.toString())
  };

  return content;
}
